<template>
    <dialog ref="preferences" class="modal">
        <form method="dialog" class="modal-box flex flex-col" style="max-width: 42rem; max-height: 90vh;">
            <!-- Header -->
            <div class="flex-shrink-0">
                <h3 class="text-lg font-bold">
                    Customize Cookie Preferences
                </h3>
            </div>

            <!-- Scrollable Content -->
            <div ref="scrollableContent" class="flex-1 overflow-y-auto py-4">
                <p class="mb-4">
                    We use cookies to help you navigate efficiently and perform certain functions. You will find detailed
                    information about all cookies below. The cookies that are categorized as
                    "Necessary" are stored on your browser as they are essential for enabling the basic functionalities of
                    the site.
                    <br>
                    We also use third-party services that help us analyze how you use this website, store your
                    preferences, and provide personalized content and advertising. You can choose to enable or disable what data
                    we collect and store for these purposes, but disabling some options may affect your browsing experience.
                </p>
                <div class="divide-y divide-base-200/40">
                    <!-- Necessary Cookies -->
                    <div class="py-4">
                        <label class="label cursor-not-allowed justify-start gap-2">
                            <input type="checkbox"
                                   class="toggle"
                                   checked
                                   name="necessary"
                                   disabled>
                            <span class="text-lg">Necessary</span>
                        </label>
                        <p class="text-sm text-base-content/70 mb-4">
                            These cookies are essential for the website to function properly and cannot be disabled.
                        </p>

                        <div class="space-y-2">
                            <div class="collapse collapse-arrow bg-base-200">
                                <label class="sr-only" for="cookie-consent">View cookie purpose.</label>
                                <input id="cookie-consent" type="radio" name="details">
                                <div class="collapse-title text-lg font-medium">
                                    Cookie:
                                    <pre class="inline-block">cookie-consent</pre>
                                </div>
                                <div class="collapse-content">
                                    <p><b class="text-primary">Purpose:</b> Remembers the user's consent choices.</p>
                                    <p>
                                        <b class="text-primary">Duration:</b> 6 months
                                    </p>
                                </div>
                            </div>
                            <div class="collapse collapse-arrow bg-base-200">
                                <label class="sr-only" for="xsrf-token">View cookie purpose.</label>
                                <input id="xsrf-token" type="radio" name="details">
                                <div class="collapse-title text-lg font-medium">
                                    Cookie:
                                    <pre class="inline-block">XSRF-TOKEN</pre>
                                </div>
                                <div class="collapse-content">
                                    <p><b class="text-primary">Purpose:</b> Used for security reasons.</p>
                                    <p><b class="text-primary">Duration:</b> 2 hours</p>
                                </div>
                            </div>
                            <div class="collapse collapse-arrow bg-base-200">
                                <label class="sr-only" for="cdc_session">View cookie purpose.</label>
                                <input id="cdc_session" type="radio" name="details">
                                <div class="collapse-title text-lg font-medium">
                                    Cookie:
                                    <pre class="inline-block">cdc_api_session</pre>
                                </div>
                                <div class="collapse-content">
                                    <p><b class="text-primary">Purpose:</b> To identify the session instance for a user.</p>
                                    <p><b class="text-primary">Duration:</b> 2 hours</p>
                                </div>
                            </div>
                            <div class="collapse collapse-arrow bg-base-200">
                                <label class="sr-only" for="id">View cookie purpose.</label>
                                <input id="id" type="radio" name="details">
                                <div class="collapse-title text-lg font-medium">
                                    Cookie:
                                    <pre class="inline-block">&lt;id&gt;</pre>
                                </div>
                                <div class="collapse-content">
                                    <p>
                                        <b class="text-primary">Purpose:</b> To store user input data temporarily for the
                                        user's convenience. Name is a 40 characters long randomised text in order to protect
                                        against session fixation attacks.
                                    </p>
                                    <p><b class="text-primary">Duration:</b> 2 hours</p>
                                </div>
                            </div>
                            <div class="collapse collapse-arrow bg-base-200">
                                <label class="sr-only" for="recaptha">View cookie purpose.</label>
                                <input id="recaptha" type="radio" name="details">
                                <div class="collapse-title text-lg font-medium">
                                    Cookie:
                                    <pre class="inline-block">_GRECAPTCHA</pre>
                                </div>
                                <div class="collapse-content">
                                    <p>
                                        <b class="text-primary">Purpose:</b> Google Recaptcha assesses user behavior on this
                                        website, collecting various software and hardware factors to be sent for analysis to
                                        Google to prevent systems cyber spam and abuse from bots.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Master Toggle for Optional Data Collection -->
                    <div v-if="hasAnalytics" class="py-4">
                        <label class="label justify-start gap-2">
                            <input v-model="allOptionalEnabled"
                                   type="checkbox"
                                   class="toggle toggle-primary"
                                   name="all-optional">
                            <span class="text-lg font-semibold">Enable All Optional Data Collection</span>
                        </label>
                        <p class="text-sm text-base-content/70 mb-4">
                            Toggle all optional data collection on or off. You can still customize individual preferences below.
                        </p>

                        <!-- Third-party Analytics Cookies -->
                        <div class="space-y-2 mb-4">
                            <div class="collapse collapse-arrow bg-base-200">
                                <label class="sr-only" for="google-analytics">View cookie purpose.</label>
                                <input id="google-analytics" type="radio" name="details">
                                <div class="collapse-title text-lg font-medium">
                                    Third-party Cookie:
                                    <pre class="inline-block">_ga</pre>
                                </div>
                                <div class="collapse-content">
                                    <p>
                                        <b class="text-primary">Purpose:</b> The _ga cookie, installed by Google Analytics,
                                        calculates visitor, session, campaign data, and also keeps track of site usage for
                                        the site's analytics report. The cookie stores information anonymously and assigns a
                                        randomly generated number to recognize unique visitors.
                                    </p>
                                    <p><b class="text-primary">Duration:</b> 2 years</p>
                                </div>
                            </div>
                            <div class="collapse collapse-arrow bg-base-200">
                                <label class="sr-only" for="google-analytics-id">View cookie purpose.</label>
                                <input id="google-analytics-id" type="radio" name="details">
                                <div class="collapse-title text-lg font-medium">
                                    Third-party Cookie:
                                    <pre class="inline-block">_ga_&lt;id&gt;</pre>
                                </div>
                                <div class="collapse-content">
                                    <p>
                                        <b class="text-primary">Purpose:</b> Installed by Google Analytics, _ga_&lt;id&gt;
                                        cookie stores information on how visitors use a website, while also creating an
                                        analytics report of the website's performance. Some of the data that are collected
                                        include the number of visitors, their source, and the pages they visit anonymously.
                                    </p>
                                    <p><b class="text-primary">Duration:</b> 2 years</p>
                                </div>
                            </div>
                        </div>

                        <div class="ml-8 space-y-3">
                            <!-- Analytics Storage -->
                            <div>
                                <label class="label justify-start gap-2">
                                    <input v-model="localPreferences.analyticsStorage"
                                           type="checkbox"
                                           class="toggle toggle-sm"
                                           name="analytics-storage">
                                    <span class="text-base">Analytics Storage</span>
                                </label>
                                <p class="text-xs text-base-content/70 ml-10">
                                    Allows us to collect anonymous analytics data to understand how visitors use our website.
                                </p>
                            </div>

                            <!-- Ad Storage -->
                            <div>
                                <label class="label justify-start gap-2">
                                    <input v-model="localPreferences.adStorage"
                                           type="checkbox"
                                           class="toggle toggle-sm"
                                           name="ad-storage">
                                    <span class="text-base">Ad Storage</span>
                                </label>
                                <p class="text-xs text-base-content/70 ml-10">
                                    Enables storage of advertising-related data such as ad clicks and conversions.
                                </p>
                            </div>

                            <!-- Ad Personalization -->
                            <div>
                                <label class="label justify-start gap-2">
                                    <input v-model="localPreferences.adPersonalization"
                                           type="checkbox"
                                           class="toggle toggle-sm"
                                           name="ad-personalization">
                                    <span class="text-base">Ad Personalization</span>
                                </label>
                                <p class="text-xs text-base-content/70 ml-10">
                                    Allows personalized advertising based on your interests and browsing behavior.
                                </p>
                            </div>

                            <!-- Ad User Data -->
                            <div>
                                <label class="label justify-start gap-2">
                                    <input v-model="localPreferences.adUserData"
                                           type="checkbox"
                                           class="toggle toggle-sm"
                                           name="ad-user-data">
                                    <span class="text-base">Ad User Data</span>
                                </label>
                                <p class="text-xs text-base-content/70 ml-10">
                                    Enables the use of user data for advertising purposes, including audience measurement and ad effectiveness.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Sticky Footer with Shadow -->
            <div class="flex-shrink-0 sticky bottom-0 bg-base-100 transition-shadow duration-200"
                 :class="{ 'shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.1)]': !arrivedState.bottom }">
                <div class="modal-action mt-0 pt-4">
                    <button type="button" class="btn btn-outline" @click="handleCancel">
                        Cancel
                    </button>
                    <button type="button" class="btn btn-primary" @click="handleSave">
                        Save Preferences
                    </button>
                </div>
            </div>
        </form>
        <form method="dialog" class="modal-backdrop">
            <button>close</button>
        </form>
    </dialog>
</template>

<script setup lang="ts">
defineOptions({
    name: 'CookiePreferences'
});

const hasAnalytics = useRuntimeConfig().public.gtag.id;
const { preferences, consentPreferences, setConsent } = useConsent();

// Template refs
const scrollableContent = ref<HTMLElement>();

// Local state for preferences that can be modified without saving
const localPreferences = ref({
    analyticsStorage: true,
    adStorage: true,
    adPersonalization: true,
    adUserData: true
});

const { arrivedState } = useScroll(scrollableContent);

// Computed property for master toggle
const allOptionalEnabled = computed({
    get: () => {
        return localPreferences.value.analyticsStorage &&
               localPreferences.value.adStorage &&
               localPreferences.value.adPersonalization &&
               localPreferences.value.adUserData;
    },
    set: (value: boolean) => {
        localPreferences.value.analyticsStorage = value;
        localPreferences.value.adStorage = value;
        localPreferences.value.adPersonalization = value;
        localPreferences.value.adUserData = value;
    }
});

// Initialize local preferences when modal opens
const initializeLocalPreferences = () => {
    localPreferences.value = { ...consentPreferences.value };
};

// Watch for when the modal opens to initialize local state
watch(preferences, (newVal) => {
    if (newVal) {
        // Modal is opening, initialize local preferences
        initializeLocalPreferences();
    }
});

const handleSave = () => {
    setConsent(localPreferences.value);
    preferences.value?.close();
};

const handleCancel = () => {
    // Reset local preferences to current saved state
    initializeLocalPreferences();
    preferences.value?.close();
};

// Initialize on component mount
onMounted(() => {
    initializeLocalPreferences();
});
</script>
